import { useCurrentFrame, interpolate, Easing } from 'remotion';

// Utils
import { combineClasses } from '@/utils/className';

interface TextFlipAnimationProps {
  startFrame: number;
  fromText: string;
  toText: string;
  fromClassName?: string;
  toClassName?: string;
  className?: string;
  perspective?: number;
  duration?: {
    fall: number;
    riseDelay: number;
    rise: number;
  };
}

const DEFAULT_DURATION = {
  fall: 20,
  riseDelay: 20,
  rise: 30,
};

/**
 * Generic 3D text flip animation component.
 * Transitions from `fromText` to `toText` using a 3D rotation effect.
 */
export const TextFlipAnimation = ({
  startFrame,
  fromText,
  toText,
  fromClassName,
  toClassName,
  className,
  perspective = 1000,
  duration = DEFAULT_DURATION,
}: TextFlipAnimationProps) => {
  const frame = useCurrentFrame();

  const { fall: fallDuration, riseDelay, rise: riseDuration } = duration;

  const progressFall = interpolate(
    frame,
    [startFrame, startFrame + fallDuration],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    },
  );

  const progressRise = interpolate(
    frame,
    [startFrame + riseDelay, startFrame + riseDelay + riseDuration],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    },
  );

  // 1. Fall Animation: "fromText" rotates backward (0 -> 90deg) and fades out
  const fallRotate = interpolate(progressFall, [0, 1], [0, 90], {
    easing: Easing.in(Easing.ease),
  });
  const fallOpacity = interpolate(progressFall, [0, 1], [1, 0], {
    easing: Easing.in(Easing.ease),
  });

  // 2. Rise Animation: "toText" rotates upward from flat (90 -> 0deg) and fades in
  const riseRotate = interpolate(progressRise, [0, 1], [90, 0], {
    easing: Easing.bezier(0.25, 1.5, 0.75, 1),
  });
  const riseOpacity = interpolate(progressRise, [0, 1], [0, 1]);

  return (
    <div
      className={combineClasses(
        `relative size-full perspective-[${perspective}px]`,
        className,
      )}
    >
      {/* Starting Text: Falling Backward */}
      <span
        className={combineClasses(
          'absolute inset-0 flex-center origin-bottom',
          fromClassName,
        )}
        style={{
          opacity: fallOpacity,
          transform: `rotateX(${fallRotate}deg)`,
        }}
      >
        {fromText}
      </span>

      {/* Ending Text: Rising Up */}
      <span
        className={combineClasses(
          'absolute inset-0 flex-center origin-bottom',
          toClassName,
        )}
        style={{
          opacity: riseOpacity,
          transform: `rotateX(${riseRotate}deg)`,
        }}
      >
        {toText}
      </span>
    </div>
  );
};
